/**
 * MarketingStrategySection — the "how we'll approach this" section.
 *
 * Shows the narrative, positioning note, guardrails, and timeline
 * phases. This is the section the owner returns to when they want
 * to remember *why* the plan looks the way it does.
 */
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import type { StrategyBlock, TimelineBlock } from "../../types";

export function MarketingStrategySection({
  strategy,
  timeline,
}: {
  strategy: StrategyBlock;
  timeline: TimelineBlock;
}): JSX.Element {
  return (
    <section data-testid="strategy-marketing-strategy">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Marketing Strategy
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          The Approach
        </h2>
      </div>

      <Card className="p-6 mb-6">
        <p className="text-base leading-relaxed">{strategy.narrative}</p>
        <p className="text-base leading-relaxed mt-4 text-muted-foreground">
          {strategy.positioning_notes}
        </p>
      </Card>

      <div className="mb-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Timeline
        </p>
        <p className="text-sm text-muted-foreground">
          {timeline.start_date} — {timeline.end_date} · {timeline.total_days} days
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-testid="strategy-phases">
        {timeline.phases.map((phase) => (
          <Card key={phase.name} className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold">{phase.name}</span>
              <Badge variant="secondary" className="text-xs">
                {phase.allocation_pct}%
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {phase.from} → {phase.to}
            </p>
            <p className="text-sm leading-relaxed">{phase.focus}</p>
          </Card>
        ))}
      </div>

      {strategy.guardrails.length > 0 && (
        <div data-testid="strategy-guardrails">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Guardrails
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {strategy.guardrails.map((g, i) => (
              <li key={i}>— {g}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
